import styled from '@emotion/styled';
import { Col, Row } from 'antd';
import moment from 'moment';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import { SurveyQuestionSwitch } from 'src/modules/programs/surveys/components/SurveyQuestionSwitch';
import {
    GenericSurveyAnswer,
    GenericSurveyQuestion,
    SurveyBooleanQuestionAnswer,
} from 'src/modules/programs/surveys/models/surveys';
import { GenericProgramSurvey } from 'src/modules/programs/surveys/models/surveysGeneric';
import {
    getProgramSurveyQuestionAnswerByRenderId,
    getProgramSurveyQuestionByRenderId,
} from 'src/modules/programs/surveys/selectors/surveysSelectors';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';

const ExposureDetails = styled(Row)({
    padding: 0,
    marginTop: '2rem',
    alignItems: 'center',
});

const ExposureTitle = styled.h5(
    {
        color: color.chathamsBlue,
        marginBottom: 0,
    },
    font.heavy(font.size.mediumExtended),
);

const ExposurePlannedDate = styled.p(
    {
        color: color.mamba,
        fontStyle: 'oblique',
    },
    font.roman(font.size.base),
);

const CompletionDetailsQuestionWrapper = styled.div({
    marginTop: '1rem',
    backgroundColor: hexToRgba(color.scooter, 0.1),
    borderRadius: 5,
    padding: '1.5rem',
});

interface StateProps {
    completionQuestion?: GenericSurveyQuestion;
    completionAnswer: SurveyBooleanQuestionAnswer | undefined;
    difficultyQuestion?: GenericSurveyQuestion;
    notDoneReasonQuestion?: GenericSurveyQuestion;
}

interface Props extends WithTranslation, StateProps {
    programId: ProgramEnum;
    surveyId: GenericProgramSurvey;
    exposureAnswers: GenericSurveyAnswer[];
}

export class ExposureCompletionStatusContainer extends React.PureComponent<Props> {
    public render(): JSX.Element {
        const {
            programId,
            surveyId,
            exposureAnswers,
            completionQuestion,
            completionAnswer,
            difficultyQuestion,
            notDoneReasonQuestion,
            t,
        } = this.props;

        const commonQuestionProps = { programId, surveyId, noShadow: true, noPadding: true };

        const exposureDetails: Record<string, string> = exposureAnswers.reduce(
            (acc, answer) =>
                answer.renderId
                    ? {
                          ...acc,
                          [answer.renderId]: answer.data.content,
                      }
                    : acc,
            {},
        );

        const isExposureCompleted = completionAnswer && completionAnswer.data.content;

        return (
            <>
                <ExposureDetails type="flex">
                    <Col sm={18}>
                        <ExposureTitle>{exposureDetails.exposureTitle}</ExposureTitle>
                        <ExposurePlannedDate>
                            {t('common.plannedFor')}{' '}
                            {moment(exposureDetails.exposureDate, 'DD-MM-YYYY').format(
                                t('inputs.dateTime.dateTimeFormat'),
                            )}
                            , {t('common.at')} {exposureDetails.exposureTime}
                        </ExposurePlannedDate>
                    </Col>
                    <Col sm={6}>
                        <SurveyQuestionSwitch
                            {...commonQuestionProps}
                            question={completionQuestion}
                        />
                    </Col>
                </ExposureDetails>
                {isExposureCompleted === true && (
                    <CompletionDetailsQuestionWrapper>
                        <SurveyQuestionSwitch
                            {...commonQuestionProps}
                            question={difficultyQuestion}
                        />
                    </CompletionDetailsQuestionWrapper>
                )}
                {isExposureCompleted === false && (
                    <CompletionDetailsQuestionWrapper>
                        <SurveyQuestionSwitch
                            {...commonQuestionProps}
                            question={notDoneReasonQuestion}
                        />
                    </CompletionDetailsQuestionWrapper>
                )}
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId, surveyId }: Props): StateProps => {
    const surveyRelatedArgs = [state, programId, surveyId] as any;

    return {
        completionQuestion: getProgramSurveyQuestionByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'completion',
        ]),
        completionAnswer: getProgramSurveyQuestionAnswerByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'completion',
        ]) as SurveyBooleanQuestionAnswer | undefined,
        difficultyQuestion: getProgramSurveyQuestionByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'difficulty',
        ]),
        notDoneReasonQuestion: getProgramSurveyQuestionByRenderId.apply(null, [
            ...surveyRelatedArgs,
            'notDoneReason',
        ]),
    };
};

export const ExposureCompletionStatus = withTranslation()(
    connect<StateProps>(mapStateToProps)(ExposureCompletionStatusContainer),
);
